import * as React from "react";
import { Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { useAuth } from "../../lib/auth/hooks";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useI18n } from "../../lib/i18n";
import * as featureFlagsApi from "../../lib/api/feature-flags";
import type { FeatureFlag } from "../../lib/api/feature-flags";
import type { ListParams } from "../../../ui/api/listApi.types";
import { createFeatureFlagsTableColumns } from "./featureFlagsTableColumns";
import { FeatureFlagModal } from "./FeatureFlagModal";
import { ALL_GROUPS, UNGROUPED, filterAndSortFlags, groupTabValues, topLevelGroup } from "./featureFlagGroups";
import * as pageStyles from "../users/UsersListPage.css";
import * as s from "./FeaturesListPage.css";

export function FeaturesListPage() {
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const { subscribe, unsubscribe } = useWebSocket();
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [modalFlagId, setModalFlagId] = React.useState<string | null | "new">(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [allFlags, setAllFlags] = React.useState<FeatureFlag[]>([]);
  const [group, setGroup] = React.useState(ALL_GROUPS);

  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  React.useEffect(() => {
    const handler = () => refresh();
    subscribe(["entity:feature-flags:global"], handler);
    return () => unsubscribe(["entity:feature-flags:global"], handler);
  }, [subscribe, unsubscribe, refresh]);

  const columns = React.useMemo(() => createFeatureFlagsTableColumns(t), [t]);

  const fetchFlags = React.useCallback(
    async (params: ListParams) => {
      if (!token) return { data: [] as FeatureFlag[], total: 0 };
      try {
        const flags = await featureFlagsApi.fetchFeatureFlags(token);
        setLoadError(null);
        setAllFlags(flags);
        const rows = filterAndSortFlags(flags, {
          group,
          search: params.search,
          sortBy: params.sort_by,
          order: params.order,
        });
        return { data: rows, total: rows.length };
      } catch (err: any) {
        setLoadError(err?.message || t("pro.system.featureFlags.error.load"));
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, group, t],
  );

  const groupTabs = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of allFlags) counts.set(topLevelGroup(f), (counts.get(topLevelGroup(f)) ?? 0) + 1);
    return [
      { value: ALL_GROUPS, label: t("pro.system.featureFlags.group.all", { count: allFlags.length }) },
      ...groupTabValues(allFlags).map((g) => ({
        value: g,
        label: `${g === UNGROUPED ? t("pro.system.featureFlags.group.ungrouped") : g} (${counts.get(g) ?? 0})`,
      })),
    ];
  }, [allFlags, t]);

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.system.featureFlags.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.featureFlags.title")}
        </Text>
      </div>

      {loadError && (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      )}

      {groupTabs.length > 2 && (
        <div className={s.groupTabs}>
          <TabSwitcher
            aria-label={t("pro.system.featureFlags.group.tabsAria")}
            value={group}
            onChange={setGroup}
            tabs={groupTabs}
          />
        </div>
      )}

      <div className={pageStyles.tableHost}>
        <ListView<FeatureFlag>
          listId="feature-flags"
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={fetchFlags}
          contextKey={`${refreshKey}:${group}`}
          fillContainer
          enabledViews={["table"]}
          searchPlaceholder={t("pro.system.featureFlags.search")}
          emptyTitle={t("pro.system.featureFlags.emptyTitle")}
          emptySubtitle={t("pro.system.featureFlags.emptySubtitle")}
          defaultSort={{ sort_by: "label", order: "asc" }}
          onRowClick={(row) => setModalFlagId(row.id)}
          headerActions={[
            {
              id: "create",
              label: "Create feature",
              icon: <Plus size={14} />,
              color: "primary",
              onClick: () => setModalFlagId("new"),
            },
          ]}
        />
      </div>

      {modalFlagId !== null && (
        <FeatureFlagModal
          flagId={modalFlagId === "new" ? null : modalFlagId}
          open={modalFlagId !== null}
          setOpen={(open) => {
            if (!open) setModalFlagId(null);
          }}
          onSaved={() => {
            setModalFlagId(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
